import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import ProductCard from "@/components/ProductCard";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";

export default function Catalog({ type, title, subtitle }) {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const category = params.get("category") || "All";

  useEffect(() => {
    api.get("/products/categories").then((r) => setCategories(["All", ...r.data])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      api
        .get("/products", { params: { type, category, q } })
        .then((r) => setProducts(r.data))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [type, category, q]);

  const setCategory = (c) => {
    const next = new URLSearchParams(params);
    if (c === "All") next.delete("category"); else next.set("category", c);
    setParams(next);
  };

  return (
    <Layout>
      <SEO title={title} description={subtitle} />
      <section className="border-b border-brand-line">
        <div className="container-narrow py-16 md:py-24">
          <span className="overline text-brand-rust" data-testid="catalog-overline">{type === "book" ? "The Library" : "The Studio"}</span>
          <h1 className="font-display text-5xl md:text-6xl mt-4 text-brand-ink">{title}</h1>
          <p className="mt-4 text-brand-inkMuted max-w-2xl leading-relaxed">{subtitle}</p>
        </div>
      </section>

      <section className="container-narrow py-10">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-10">
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setCategory(c)}
                className={`overline px-4 py-2 border transition-colors ${
                  category === c
                    ? "bg-brand-ink text-white border-brand-ink"
                    : "bg-white text-brand-ink border-brand-line hover:border-brand-ink"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="relative max-w-xs w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-inkMuted" />
            <Input
              data-testid="catalog-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search…"
              className="pl-9 rounded-none border-brand-line focus:border-brand-ink focus:ring-0"
            />
          </div>
        </div>

        {loading ? (
          <div className="overline text-brand-inkMuted py-20 text-center">Loading…</div>
        ) : products.length === 0 ? (
          <div className="overline text-brand-inkMuted py-20 text-center" data-testid="catalog-empty">No results.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {products.map((p, i) => <ProductCard key={p.product_id} product={p} index={i} />)}
          </div>
        )}
      </section>
    </Layout>
  );
}
